<template>
    <!-- Continuous GlassTimeline rail at the chassis floor. The prior
         `#detail` scoped slot (per-segment numeric chip) retired at
         AJ-W1-α-9 per user direction P1-6 — the hero column owns the
         live phase value at audacious-poster register, so the chip's
         second-rendering of the same data was redundant and the
         per-frame width churn jittered the screen. Echo opacity (below)
         supplies the post-complete contrast. -->
    <div class="timeline-wrap" :data-engine-state="engineState">
        <!-- No `data-phase` here: the `[data-phase]` resolver in
             `tokens.css` only matches the measured phase keys
             (`ping` / `download` / `upload`). The timeline no longer
             carries a per-segment `--phase-color` cascade (the consumer
             surface that read it retired with the `#detail` slot at
             AJ-W1-α-9). -->
        <div class="timeline-region tap-squish">
            <GlassTimeline
                variant="continuous"
                :segments="timelineSegments"
                :current-segment-key="currentSegmentKey"
                aria-label="Speedtest progress"
                @click="onTimelineClick"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { GlassTimeline } from "@mkbabb/glass-ui/timeline";
// Type-only via `/api` so the consumer's type graph doesn't drag the
// Vue runtime.
import type { TimelineSegment } from "@mkbabb/glass-ui/api";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";
import type { MetricId } from "@src/types/speedtest.ts";
import { useEngineState } from "@features/speedtest/composables/useEngineState.ts";
import { useMetricRegistry } from "@features/speedtest/composables/useMetricRegistry.ts";
import { useMetricStateMachine } from "@features/speedtest/composables/useMetricStateMachine.ts";
import { handoffRemap } from "@features/speedtest/composables/meter/math.ts";
import { usePhaseTransition } from "@features/speedtest/composables/usePhaseTransition.ts";

// T2 — prop-chain deleted; currentTestProgress is store-derived at the leaf.
// TraditionalSpeedtest → SpeedtestResults → PhaseTimeline was pure forwarding;
// the store-derive below is now the only read path.

// Pinia-store-backed singletons — shared with the composition root.
const speedtest = useSpeedtestStore();
const registry = useMetricRegistry();
const stateMachine = useMetricStateMachine();

// W5-C1 — the timeline is one channel of the unified phase handoff. The
// completing segment reaches `state: "completed"` (+ `progress: 1`) at
// the boundary, which fires glass-ui's W1 self-drawing completion tick —
// the FIRST beat of the handoff. The incoming segment then fills from 0
// across the ≈600ms handoff clock rather than snapping, so the tick and
// the next segment's fill read as one connective gesture.
const handoff = usePhaseTransition();

// `data-engine-state` lives on `.timeline-wrap` as a Playwright /
// diagnostic hook; derivation is shared via `useEngineState()` so the
// timeline and the chassis stay token-coupled.
const engineState = useEngineState();

// Per-segment state from useMetricStateMachine; gradient from
// MetricDef.color; progress from per-phase telemetry. ping + jitter
// share the engine phase; jitter maps to "ping" for the progress
// lookup. DPI variant collapses count via `registry.visible`.
type SegState = "pending" | "active" | "completed";

function segmentStateFor(metricId: MetricId): SegState {
    if (stateMachine.isFinished(metricId).value) return "completed";
    if (stateMachine.isActiveOrStarted(metricId).value) return "active";
    return "pending";
}

/**
 * Per-segment fill fraction.
 *
 * - `completed` → 1 (fires the W1 self-drawing tick).
 * - `active` for the live phase → the live per-phase progress, EXCEPT
 *   while a handoff into this phase runs: then the fill is the handoff
 *   clock's own ramp, so the incoming segment grows from 0 across the
 *   ≈600ms window instead of snapping (W5-C1 connective tissue).
 * - otherwise → 0.
 */
function segmentProgressFor(metricId: MetricId, state: SegState): number {
    if (state === "completed") return 1;
    const myPhase = metricId === "jitter" ? "ping" : metricId;
    if (state !== "active" || speedtest.currentEnginePhase !== myPhase)
        return 0;

    // T2 — store-derived: read progress directly (prop-chain deleted).
    // myPhase === speedtest.currentEnginePhase is guaranteed by the guard above.
    const storeProgress = speedtest.getSpeedtestStateAmount(
        myPhase,
        "Progress",
    );
    const live = Math.max(0, Math.min(1, storeProgress));
    // Handoff INTO this segment — ramp the fill on the shared clock so it
    // emerges after the completing segment's tick beat. The two-beat shape
    // is the canonical handoffRemap at origin 0, riding the meter's declared
    // 1/3 split — the third locus of the shape W11b.b unified.
    if (handoff.active.value && handoff.toPhase.value === myPhase) {
        return handoffRemap(handoff.progress.value, 0, live);
    }
    return live;
}

const timelineSegments = computed<TimelineSegment[]>(() =>
    registry.visible.value.map((metric) => {
        const state = segmentStateFor(metric.id);
        const progress = segmentProgressFor(metric.id, state);
        const colorHex = metric.color;
        return {
            key: metric.id,
            label: metric.label,
            state,
            progress,
            gradient: {
                from: `color-mix(in srgb, ${colorHex} 70%, transparent)`,
                to: colorHex,
            },
        };
    }),
);

// AJ-W1-α-9 — the `#detail` scoped-slot consumer surface retired
// (along with its hover-resolved `effectiveSegment` consumption and
// the `--phase-color` cascade lookup). The primitive still owns its
// internal hover state + effective-segment resolution; only the
// `current-segment-key` prop binding survives, driving the rail's own
// segment-highlight + click-stick pin state below.

/** Engine-derived current phase key. Jitter folds into ping at the
 *  engine layer; the segment-key keeps metric-side identity for the
 *  visible-registry. */
const engineSegmentKey = computed<string | undefined>(() => {
    const phase = speedtest.currentEnginePhase;
    if (!phase) return undefined;
    // Match against the visible-metric registry so DPI-collapsed
    // variants still resolve correctly.
    const visibleKeys = new Set(registry.visible.value.map((m) => m.id));
    if (visibleKeys.has(phase)) return phase;
    // ping engine phase covers jitter when the variant collapses.
    if (phase === "ping" && visibleKeys.has("jitter")) return "jitter";
    return undefined;
});

// AI.W4-M.2 (W1-δ Q1 click-stick resolution) — `pinnedSegmentKey` is
// the sticky-on-click override. Clicking a segment pins the detail-
// slot to that segment-key; the pin survives engine-phase advances so
// the user can interrogate a completed phase without the live phase
// stealing focus back. The pin clears when:
//   - the user clicks the SAME pinned segment (toggle off);
//   - the engine returns to idle (no `currentEnginePhase`) — a fresh
//     run starts from a clean slate;
//   - the pinned segment-key is no longer in the visible registry (a
//     variant change retires the metric, e.g. ping→jitter collapse).
// The pin is consumer-side state (no glass-ui primitive change); the
// primitive resolves `effective = hovered ?? current` against the
// `current-segment-key` prop, which is exactly the override surface
// the click-stick needs. Hover still takes precedence so a sticky pin
// doesn't block interrogation of an adjacent segment.
const pinnedSegmentKey = ref<string | undefined>(undefined);

const currentSegmentKey = computed<string | undefined>(
    () => pinnedSegmentKey.value ?? engineSegmentKey.value,
);

// Clear the pin on idle (fresh run) or when the pinned key retires
// from the visible registry.
watch(
    [engineSegmentKey, () => registry.visible.value.map((m) => m.id).join(",")],
    () => {
        if (pinnedSegmentKey.value == null) return;
        if (speedtest.currentEnginePhase == null) {
            pinnedSegmentKey.value = undefined;
            return;
        }
        const visible = new Set<string>(
            registry.visible.value.map((m) => m.id),
        );
        if (!visible.has(pinnedSegmentKey.value)) {
            pinnedSegmentKey.value = undefined;
        }
    },
);

function onTimelineClick(payload: { key: string; segment: TimelineSegment }) {
    // AI.W4-M.2 (W1-δ Q1) — click-stick. The pin survives engine-
    // advances (so the user can interrogate a completed phase without
    // the live phase stealing focus back); clicking the SAME pinned
    // key clears the pin (a familiar toggle gesture). Hover still
    // wins on the next pointer-move via the primitive's
    // `effective = hovered ?? current` resolution.
    pinnedSegmentKey.value =
        pinnedSegmentKey.value === payload.key ? undefined : payload.key;
}
</script>

<style scoped>
/* W17 (D8 / scope 8) — the OVERALL chassis-floor rail is the card's
   bottom-edge journey rail. It reads at the single design-home
   `--progress-height` weight (matched to the survey dock rail) so the
   "overall progress bar" the user named reads as a LEGIBLE primary rail —
   distinct from the under-dial per-phase bar (the two-frequency read: the
   per-phase bar is the thicker ~13px live cap, the overall rail is the
   ~6px card-edge journey line). The phase colours + ordering carry the
   cross-phase map. */
.timeline-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    /* Pin the timeline to the panel floor: as the last flex child of the
       `.results-card` column (and `grid-row: 2` at ≥720px), an `auto`
       block-start margin pushes it to the bottom edge so the rail
       sits on the chassis floor rather than floating mid-card. */
    margin-block-start: auto;
    /* W17 (D8) — LIFT the fill opacity from the prior 0.58 echo-tier to a
       LEGIBLE primary rail. The spec mandate (scope 8): lift the OVERALL
       timeline "from the low-opacity hairline to a legible primary rail,
       visually distinct from the per-phase bar." 0.58 read as a dead,
       low-opacity thread (the user: the overall rail never reads). 0.85
       seats it as a confident journey line that still reads one rung below
       the live under-dial cap; hover/focus lifts to 1 for interrogation.
       glass-ui's continuous-track-wrap consumes this cascade variable — no
       `:deep` reach into the primitive's internal selector tree. */
    --continuous-fill-opacity: 0.85;
    /* W17 (D8) — REVERSAL of AS-15's 2px hairline (overall bar only).
       ──────────────────────────────────────────────────────────────────
       WHAT: `--timeline-continuous-height: 2px` → `var(--progress-height)`
             (the single design-home rail-weight token, ~6px).
       WHY: AS-15 thinned the rail to a 1–2px hairline "reading as the
            meter-card's bottom EDGE". The W17 cross-surface lens (scope 8 /
            DESIGN-INTAKE-3) supersedes: the chassis-floor journey rail
            becomes part of the card BORDER at a MATCHED thickness — the
            same `--progress-height` weight the survey dock rail carries, so
            "card-floor rail at matched thickness" reads as ONE weight
            across the surfaces. A 2px hairline at 0.58 opacity was the
            "low-opacity hairline" the user named as not reading; the lift
            to the border weight + 0.85 opacity is the legible-primary rail
            the mandate calls for. Still reads as the card's bottom EDGE
            (AS-15's spatial intent preserved) — now at border weight, not a
            dead thread.
       ALTERNATIVE REJECTED: keep 2px and only lift opacity. Rejected — the
            mandate is an explicit thickness match to the card border /
            `--progress-height`, not an opacity-only nudge; 2px cannot read
            as a border at the card scale.
       RE-LITIGATE-IF: the border-bottom MIGRATION lands (the rail folded
            INTO `border-bottom` via the routed ASK-GU-TIMELINE-MARKER-
            OPTOUT — still :deep()-gated, precept-6), at which point this
            height token feeds the border-width directly.
       REVERSAL of AS-15 (the 2px hairline DDR). */
    --timeline-continuous-height: var(--progress-height);

    /* AC.W9 (B2) — the continuous rail is now ONE stitched gradient: the
       phase hues cross-fade smoothly across the boundaries. Suppress the
       primitive's per-boundary seam dividers entirely so nothing breaks
       the stitch. */
    --timeline-continuous-seam-opacity: 0;

    /* AC.W9 (B3) — the timeline dots are glassy, NOT green. glass-ui's
       continuous-dot is a glass primitive (translucent fill + backdrop
       blur + hairline ring); these tint hooks keep the per-state read
       neutral so the dot stays pure glass rather than picking up a
       green `--success` / `--accent` wash. */
    --timeline-dot-tint-completed: var(--foreground);
    --timeline-dot-tint-current: var(--foreground);
}
.timeline-wrap:hover,
.timeline-wrap:focus-within {
    --continuous-fill-opacity: 1;
}

/* AE.W1 Cluster B — retire the timeline once the run completes. The
   bottom timeline is OVERALL progress; once the run has finished it is
   meaningless dead chrome, and it occupies the band the complete-screen
   result stack needs. The retirement choreographs on the same
   `data-engine-state` seam the meter dial-out uses
   (SpeedtestResults.vue:486-493): a fade over `--duration-slow`, then the
   box drops from hit-testing + paint so the result stack reads cleanly.

   I gate on `visibility: hidden` after the fade — NOT `display: none`.
   `display: none` at `complete` would re-introduce CLS on the
   running → complete transition (the grid row collapses instantly);
   `visibility: hidden` keeps the box but removes it from paint +
   hit-testing once the fade lands. The `visibility 0s linear
   var(--duration-slow)` arm holds `visible` through the opacity fade,
   then flips — the same deferred-visibility pattern
   `SpeedtestResults.vue` uses for `.complete-wrap`. `data-engine-state`
   is the live `useEngineState()` derivation, already bound to
   `.timeline-wrap`. */
.timeline-wrap {
    opacity: 1;
    transition: opacity var(--duration-slow) var(--ease-out);
}
.timeline-wrap[data-engine-state="complete"] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    transition:
        opacity var(--duration-slow) var(--ease-out),
        visibility 0s linear var(--duration-slow);
}

/* PRM bracket — under a reduced-motion preference the retirement is an
   instant state change, not a fade. The timeline still retires (it is
   meaningless post-complete); only the motion is dropped. */
@media (prefers-reduced-motion: reduce) {
    .timeline-wrap {
        transition: none;
    }
    .timeline-wrap[data-engine-state="complete"] {
        transition: none;
    }
}

/* AC.W9 (B1) — the bottom progressbar sits TIGHT against the panel's
   lower edge: no top margin, a slim row gap, and zero block padding so
   the rail affixes flush to the chassis floor.

   AJ-W1-α-9 — the `#detail` slot retired (per user direction P1-6);
   the primitive's `<ContinuousTimeline>` may still mount an empty
   `.continuous-detail` wrapper that reserves vertical space via
   `--timeline-detail-min-height` (default ≈1.25rem). Collapse the
   reservation here so the rail seats flush at the chassis floor without
   a phantom gap. The override is the AC.W6d-canonical cascade-variable
   pattern — no `:deep` reach.

   AE.W1 Cluster C — `GlassTimeline`'s `continuous` variant centres an
   endpoint MARKER dot on each track end, so half the dot (~7px) straddles
   PAST the track's inline edge. That decorative overhang is painted
   geometry that inflates every ancestor's `scrollWidth` (the card
   measured +8px of horizontal overflow on mobile). The honest fix is to
   reserve room for the timeline's own endpoint markers: a symmetric
   `padding-inline` insets the track by the marker half-width so the
   endpoint dots straddle a point INSIDE the box — fully visible, nothing
   clipped, and nothing overflows. This is geometry the timeline always
   needed; it is not a clip band-aid. */
.timeline-region {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
    margin: 0;
    padding-block: 0;
    padding-inline: 0.5rem;
    --timeline-detail-min-height: 0;
}

/* GLASS-UI CONTRACT — `<GlassTimeline variant="continuous">` emits
   `.timeline-row` as its child-row class. This consumer-side rule zeroes
   the row's inline padding for edge-to-edge timeline rendering. If
   glass-ui renames the class or moves the row deeper in its DOM (past
   the scoped-CSS `data-v-*` stamp on the primitive's root), this rule
   silently drops. Track the contract upstream as a candidate for a
   `--timeline-row-padding-inline` custom-prop knob on `<GlassTimeline>`
   so the structural reach evaporates into the AC.W6d cascade-substitution
   pattern. (FA-5 §2.4) */
.timeline-region .timeline-row {
    padding: 0;
}
</style>
